import { useEffect, useState } from "react";
import { Package, LogIn, Truck, CheckCircle2, ExternalLink, UserCircle, Save, Gamepad2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { googleLogin } from "@/components/Navbar";
import Reveal from "@/components/Reveal";

const profileInputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

function ProfileCard() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.profile_name || user?.name || "",
    address: user?.profile_address || "",
    discord: user?.profile_discord || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/member/profile", form);
      setUser({ ...user, profile_name: form.name, profile_address: form.address, profile_discord: form.discord });
      toast.success("Profil enregistré — votre adresse sera pré-remplie au prochain achat");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-tactical grid gap-4 p-6" data-testid="member-profile-card">
      <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
        <UserCircle className="h-5 w-5 text-orange-500" /> Mon profil membre
      </p>
      <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Nom complet (livraison)
          <input value={form.name} data-testid="profile-name-input"
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={profileInputCls + " mt-1"} />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
          <span className="flex items-center gap-1.5"><Gamepad2 className="h-3.5 w-3.5 text-orange-500" /> Pseudo Discord</span>
          <input placeholder="pseudo#0000" value={form.discord} data-testid="profile-discord-input"
            onChange={(e) => setForm({ ...form, discord: e.target.value })} className={profileInputCls + " mt-1"} />
        </label>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-400 sm:col-span-2">
          Adresse de livraison enregistrée
          <textarea value={form.address} rows={2} data-testid="profile-address-input"
            onChange={(e) => setForm({ ...form, address: e.target.value })} className={profileInputCls + " mt-1"} />
        </label>
        <button type="submit" disabled={saving} data-testid="profile-save-button"
          className="flex w-fit items-center gap-2 rounded-lg bg-orange-500 px-5 py-2 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 disabled:opacity-50 sm:col-span-2">
          <Save className="h-4 w-4" /> {saving ? "Enregistrement..." : "Enregistrer mon profil"}
        </button>
      </form>
    </div>
  );
}

const STATUS_STYLES = {
  "payée": "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  "confirmée": "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  "en préparation": "border-orange-500/40 bg-orange-500/10 text-orange-400",
  "expédiée": "border-cyan-400/40 bg-cyan-400/10 text-cyan-300",
  "livrée": "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  "remboursée": "border-slate-500/40 bg-slate-500/10 text-slate-300",
  "annulée": "border-red-500/40 bg-red-500/10 text-red-400",
  "en attente": "border-orange-500/40 bg-orange-500/10 text-orange-400",
  "en attente de paiement": "border-orange-500/40 bg-orange-500/10 text-orange-400",
};

const STEPS = ["Confirmée", "En préparation", "Expédiée", "Livrée"];

function stepIndex(status) {
  if (["payée", "confirmée", "en attente"].includes(status)) return 0;
  if (status === "en préparation") return 1;
  if (status === "expédiée") return 2;
  if (status === "livrée") return 3;
  return -1;
}

function Stepper({ status }) {
  const current = stepIndex(status);
  if (current < 0) return null;
  return (
    <div className="mt-4 flex items-center" data-testid="order-stepper">
      {STEPS.map((label, i) => (
        <div key={label} className="flex flex-1 items-center last:flex-none">
          <div className="flex flex-col items-center">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
              i <= current ? "border-orange-500 bg-orange-500 text-[#060B18]" : "border-slate-600 text-slate-500"
            }`}>
              {i < current || i === 3 && current === 3 ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`mt-1 text-[9px] font-bold uppercase tracking-wide ${i <= current ? "text-orange-400" : "text-slate-600"}`}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`mx-1 mb-4 h-0.5 flex-1 ${i < current ? "bg-orange-500" : "bg-slate-700"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/member/orders").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (user === null || loading) {
    return <div className="py-24 text-center text-slate-400">Chargement...</div>;
  }

  if (!user) {
    return (
      <div data-testid="my-orders-login" className="hero-grid flex min-h-[60vh] items-center justify-center px-4 py-16">
        <div className="card-tactical max-w-md p-8 text-center">
          <Package className="mx-auto h-12 w-12 text-orange-500" />
          <h1 className="mt-4 text-2xl font-extrabold uppercase">Suivi de commande</h1>
          <p className="mt-2 text-sm text-slate-400">
            Connectez-vous avec le compte Google utilisé lors de votre commande pour suivre vos achats.
          </p>
          <button onClick={googleLogin} data-testid="my-orders-google-login"
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-xl border border-slate-600 bg-white px-6 py-3 text-sm font-bold text-slate-800 transition-all hover:scale-[1.02] hover:bg-slate-100">
            <LogIn className="h-5 w-5" /> Continuer avec Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="my-orders-page" className="page-enter mx-auto max-w-4xl px-4 py-16 sm:px-8 lg:py-24">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Espace membre</p>
        <h1 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">Mes commandes</h1>
        <p className="mt-2 text-sm text-slate-400">Connecté : {user.email}</p>
      </Reveal>
      <div className="mt-8">
        <ProfileCard />
      </div>
      <div className="mt-10 grid gap-4">
        {orders.length === 0 && (
          <p className="card-tactical p-6 text-sm text-slate-400" data-testid="my-orders-empty">
            Aucune commande trouvée pour {user.email}. Les commandes passées avec cette adresse email apparaîtront ici.
          </p>
        )}
        {orders.map((o, i) => {
          const orderItems = o.items?.length
            ? o.items
            : [{ product_name: o.product_name, size: o.size, quantity: o.quantity, flocage: o.flocage, pseudo: o.pseudo }];
          const title = orderItems.length > 1 ? `Commande — ${orderItems.length} articles` : orderItems[0].product_name;
          return (
          <Reveal key={o.id} delay={i * 0.06} y={20}>
            <div className="card-tactical p-5" data-testid={`my-order-${o.id}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="min-w-[180px] flex-1">
                  <p className="font-heading text-lg font-bold uppercase">{title}</p>
                  {orderItems.map((it, j) => (
                    <p key={j} className="text-xs text-slate-400" data-testid={`my-order-item-${o.id}-${j}`}>
                      {orderItems.length > 1 ? `${it.product_name} — ` : ""}Taille {it.size} × {it.quantity}
                      {it.flocage && it.pseudo ? ` — Flocage : ${it.pseudo}` : ""}
                    </p>
                  ))}
                  <p className="mt-1 text-[11px] text-slate-500">
                    Réf. {o.id.slice(0, 8).toUpperCase()} — {new Date(o.created_at).toLocaleDateString("fr-FR")}
                    {o.shipping ? ` — Livraison : ${o.shipping.label}` : ""}
                  </p>
                </div>
                <p className="font-mono2 font-bold text-orange-500">{o.total.toFixed(2)} €</p>
                <span className={`rounded-md border px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${STATUS_STYLES[o.status] || STATUS_STYLES["en attente"]}`}>
                  {o.status}
                </span>
              </div>
              <Stepper status={o.status} />
              {o.tracking && (
                <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-cyan-400/20 bg-cyan-400/5 p-3" data-testid={`my-order-tracking-${o.id}`}>
                  <Truck className="h-5 w-5 text-cyan-300" />
                  <p className="text-xs text-slate-300">
                    {o.carrier_label ? `${o.carrier_label} — ` : ""}Suivi : <span className="font-mono2 font-bold text-cyan-300">{o.tracking}</span>
                  </p>
                  {o.tracking_url && (
                    <a href={o.tracking_url} target="_blank" rel="noreferrer" data-testid={`my-order-tracking-link-${o.id}`}
                      className="ml-auto flex items-center gap-1.5 rounded-lg bg-cyan-400/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-cyan-300 transition-all hover:scale-[1.02] hover:bg-cyan-400/25">
                      Suivre le colis <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </Reveal>
          );
        })}
      </div>
    </div>
  );
}
